/**
 * Personalization preference storage.
 *
 * Privacy rules:
 *  - The selection lives in this browser's localStorage only. It is never sent
 *    to a server, never attached to an account, and no account is required.
 *  - Only two short identifiers are stored: the prompt mode ("goal" or "role")
 *    and the option id the visitor clicked. Nothing else — no free text, no
 *    clinical credential, no health information, no identity.
 *  - Clearing removes the record entirely, restoring the default experience.
 *  - Analytics record the option id only, never a person.
 */

import { useCallback, useEffect, useState } from "react";
import { findProfile, type PersonalizationMode, type Profile } from "@/content/personalization";
import { trackEvent } from "@/lib/telemetry";

const STORAGE_KEY = "datasyte.personalization.v1";
const DISMISS_KEY = "datasyte.personalization.dismissed.v1";
const CHANGE_EVENT = "datasyte:personalization";

export type Selection = { mode: PersonalizationMode; id: string };

const read = (): Selection | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Selection>;
    if (parsed?.mode !== "goal" && parsed?.mode !== "role") return null;
    if (typeof parsed.id !== "string" || !findProfile(parsed.mode, parsed.id)) return null;
    return { mode: parsed.mode, id: parsed.id };
  } catch {
    return null;
  }
};

const notify = () => window.dispatchEvent(new Event(CHANGE_EVENT));

export const usePersonalization = () => {
  const [selection, setSelection] = useState<Selection | null>(null);
  const [dismissed, setDismissed] = useState(true);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const sync = () => {
      setSelection(read());
      try {
        setDismissed(localStorage.getItem(DISMISS_KEY) === "1");
      } catch {
        setDismissed(false);
      }
      setReady(true);
    };
    sync();
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const select = useCallback((mode: PersonalizationMode, id: string) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ mode, id }));
      localStorage.removeItem(DISMISS_KEY);
    } catch {
      /* private mode: personalization applies for this view only */
    }
    setSelection({ mode, id });
    setDismissed(false);
    trackEvent("personalization_selected", id, { kind: mode, topic: id });
    notify();
  }, []);

  const clear = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(DISMISS_KEY);
    } catch {
      /* nothing stored */
    }
    setSelection(null);
    setDismissed(false);
    trackEvent("personalization_cleared");
    notify();
  }, []);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* private mode */
    }
    setDismissed(true);
    trackEvent("personalization_dismissed");
    notify();
  }, []);

  const profile: Profile | undefined = selection ? findProfile(selection.mode, selection.id) : undefined;

  return { ready, selection, profile, dismissed, select, clear, dismiss };
};
